import { CONDITION_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { router } from "expo-router";
import { useEffect, useId, useState } from "react";

import { Button } from "~/components/Button";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { RadioSelector } from "~/components/form/radio/RadioSelector";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { Space } from "~/constants/sizing";
import type { Condition } from "~/entities/medical/condition";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useMedical } from "~/providers/MedicalProvider";
import { elementsMatch } from "~/util/array";

type ConditionFormProps = {
    readonly condition?: Condition;
    onRequestClose: () => void;
} & FullPageModalProps;

export const ConditionForm = ({ condition, onRequestClose, ...modalProps }: ConditionFormProps) => {
    const { canUpdateMedical, storeMedical } = useMedical();
    const { saving, setSaving } = useFeedback();
    const [option, setOption] = useState<string | null>(condition?.option_id ?? null);
    const [description, setDescription] = useState(condition?.description ?? "");
    const [confirmed, setConfirmed] = useState(condition?.confirmed ?? true);
    const [primary, setPrimary] = useState(condition?.primary ?? false);
    const [photographs, setPhotographs] = useState<string[]>(condition?.photograph_ids ?? []);
    const [dirty, setDirty] = useState(false);
    const id = useId();

    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (condition?.option_id ?? null) ||
                confirmed !== (condition?.confirmed ?? true) ||
                description !== (condition?.description ?? "") ||
                primary !== (condition?.primary ?? false) ||
                !elementsMatch(photographs, condition?.photograph_ids ?? []),
        );
    }, [condition, option, description, confirmed, photographs, primary]);

    const canSave = canUpdateMedical(MedicalType.condition) && !saving && option !== null;

    const doSave = () => {
        if (!canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        storeMedical({
            type: MedicalType.condition,
            id: condition?.id,
            option_id: option,
            photograph_ids: photographs,
            description,
            confirmed,
            primary: confirmed && primary,
        })
            .then((id) => {
                id &&
                    setTimeout(
                        () => router.dismissTo({ pathname: `${CONDITION_BASE_URL}/[id]/show`, params: { id } }),
                        10,
                    );
            })
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            title={`${condition?.id ? "Edit" : "Add"} condition`}
            cancelable
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
            onRequestClose={onRequestClose}
            gap={Space.lg}
            dirty={dirty}
        >
            <FieldGroup>
                <OptionSelector
                    type={OptionType.conditions}
                    value={option}
                    onItemSelected={setOption}
                    label="Condition"
                    disabled={is_saving}
                    name="option_id"
                />
                <RadioSelector
                    title="Is this a known or suspected condition?"
                    options={[
                        { value: "known", label: "Known" },
                        { value: "suspected", label: "Suspected" },
                    ]}
                    disabled={is_saving}
                    value={confirmed ? "known" : "suspected"}
                    onValueChange={(v: string) => setConfirmed(v === "known")}
                    columns={2}
                />
                {confirmed && (
                    <CheckboxInput
                        value={primary}
                        onValueChange={setPrimary}
                        label="This is my primary condition"
                        disabled={is_saving}
                    />
                )}
            </FieldGroup>
            <FieldGroup>
                <TextArea
                    value={description}
                    onChangeText={setDescription}
                    label="Description"
                    optional
                    disabled={is_saving}
                    name="description"
                />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
